'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getStoresTableColumns } from './stores-table-columns';
import { StoresTableActionBar } from './stores-table-action-bar';
import { StoresTableSkeleton } from './StoresTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import { Store } from '@/lib/api/stores';

interface StoresTableProps {
  stores: Store[];
  total: number;
  loading: boolean;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch?: () => void;
  config?: TableConfig;
}

/**
 * StoresTable component for displaying stores with DataTable functionality
 */
export function StoresTable({
  stores,
  total,
  loading,
  onTableStateChange,
  onRefetch,
  config = {},
}: StoresTableProps) {
  const { t, i18n } = useTranslation('stores');
  const navigate = useNavigate();
  const { orgSlug, orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (store: Store) => {
      navigate(orgRoutes.STORE(store.id));
    },
    [navigate, orgRoutes],
  );

  const columns = React.useMemo(
    () =>
      getStoresTableColumns({
        t,
        orgSlug,
      }),
    [t, orgSlug, i18n.language],
  );

  const { table } = useDataTable({
    data: stores,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: Store) => row.id,
  });

  return (
    <DataTable
      key={i18n.language}
      table={table}
      actionBar={<StoresTableActionBar table={table} onRefetch={onRefetch} />}
      isLoading={loading}
      skeletonRows={<StoresTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={handleRowClick}
    />
  );
}
